import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, ChevronDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { ROUTES } from '../../utils/constants';

export type MarketingNavAnchor = { label: string; href: string };

export type MarketingNavItem =
  | { type: 'anchor'; label: string; href: string }
  | { type: 'route'; label: string; to: string }
  | { type: 'group'; label: string; items: MarketingNavAnchor[] };

const DEFAULT_NAV: MarketingNavItem[] = [
  { type: 'anchor', label: 'Home', href: '#top' },
  {
    type: 'group',
    label: 'Features',
    items: [
      { label: 'AI Tutor', href: '#ai-tutor' },
      { label: 'Picture Talk', href: '#picture-talk' },
      { label: 'How it works', href: '#how-it-works' },
      { label: 'Modules', href: '#modules' },
      { label: 'Tracking', href: '#analytics' },
      { label: 'Teachers', href: '#teachers' },
      { label: 'Vocabulary', href: '#vocabulary' },
      { label: 'Safety', href: '#safety' },
    ],
  },
  { type: 'route', label: 'Pricing', to: ROUTES.PRICING },
  { type: 'route', label: 'Contact', to: ROUTES.CONTACT },
];

type Props = {
  onSubpage?: boolean;
  navItems?: MarketingNavItem[];
  onAnchorClick?: (hash: string) => void;
};

export const MarketingSiteNav: React.FC<Props> = ({
  onSubpage = false,
  navItems = DEFAULT_NAV,
  onAnchorClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [navOpen, setNavOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);

  const scrollToAnchor = (href: string) => {
    setNavOpen(false);
    setFeaturesOpen(false);
    setMobileFeaturesOpen(false);
    const id = href.startsWith('#') ? href.slice(1) : href;
    if (onSubpage) {
      navigate(`${ROUTES.LANDING}${href === '#top' ? '' : href}`);
      return;
    }
    if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (onAnchorClick) {
      onAnchorClick(id);
      return;
    }
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRoute = (to: string) => {
    setNavOpen(false);
    setFeaturesOpen(false);
    navigate(to);
  };

  useEffect(() => {
    if (!featuresOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (featuresRef.current && !featuresRef.current.contains(e.target as Node)) {
        setFeaturesOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [featuresOpen]);

  const isLanding = location.pathname === ROUTES.LANDING;

  const navLinkClass =
    'shrink-0 rounded-lg px-2.5 py-2 text-sm font-medium text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800 hover:text-app-text dark:hover:text-white transition-colors';

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-app-border dark:border-gray-800 bg-app-surface/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-app-surface/80 dark:supports-[backdrop-filter]:bg-gray-900/80">
        <div className="mx-auto grid max-w-6xl grid-cols-[auto_1fr_auto] items-center gap-4 px-4 h-[72px] sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => (isLanding && !onSubpage ? scrollToAnchor('#top') : navigate(ROUTES.LANDING))}
            className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-app-surface-muted dark:hover:bg-gray-800 transition-colors"
            aria-label="TKM Home"
          >
            <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl overflow-hidden bg-white ring-1 ring-black/10 shadow-[0_10px_24px_rgba(0,0,0,0.18)]">
              <img src="/images/tkm-logo.png" alt="TKM Logo" className="h-full w-full object-contain p-1" draggable={false} />
            </span>
            <div className="hidden md:block leading-tight text-left">
              <div className="text-base font-extrabold text-app-text dark:text-white">Tamil Katral Maiyam</div>
            </div>
          </button>

          <div className="hidden lg:flex items-center justify-center gap-1">
            {navItems.map((item) => {
              if (item.type === 'anchor') {
                return (
                  <button key={item.label} type="button" onClick={() => scrollToAnchor(item.href)} className={navLinkClass}>
                    {item.label}
                  </button>
                );
              }
              if (item.type === 'route') {
                return (
                  <Link key={item.label} to={item.to} className={navLinkClass}>
                    {item.label}
                  </Link>
                );
              }
              return (
                <div key={item.label} ref={featuresRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setFeaturesOpen((o) => !o)}
                    className={`${navLinkClass} inline-flex items-center gap-1`}
                    aria-expanded={featuresOpen}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${featuresOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {featuresOpen ? (
                    <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-xl border border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-900 py-2 shadow-xl">
                      {item.items.map((sub) => (
                        <button
                          key={sub.label}
                          type="button"
                          onClick={() => scrollToAnchor(sub.href)}
                          className="block w-full text-left px-4 py-2.5 text-sm font-medium text-app-text dark:text-gray-200 hover:bg-app-surface-muted dark:hover:bg-gray-800"
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={() => navigate(`${ROUTES.LOGIN}?mode=admin`)}>
              School login
            </Button>
            <Button size="sm" className="hidden sm:inline-flex" onClick={() => navigate(ROUTES.PRICING)}>
              View plans
            </Button>
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              className="lg:hidden p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      {navOpen ? (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden />
          <div className="fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] bg-app-surface dark:bg-gray-900 border-l border-app-border dark:border-gray-800 shadow-xl lg:hidden flex flex-col py-6 px-4 overflow-y-auto">
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={toggleTheme} className="p-2 rounded-lg" aria-label="Toggle theme">
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              <button type="button" onClick={() => setNavOpen(false)} className="p-2 rounded-lg" aria-label="Close menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col gap-1 mt-4">
              {navItems.map((item) => {
                if (item.type === 'anchor') {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => scrollToAnchor(item.href)}
                      className="text-left px-4 py-3 rounded-lg font-medium hover:bg-app-surface-muted dark:hover:bg-gray-800"
                    >
                      {item.label}
                    </button>
                  );
                }
                if (item.type === 'route') {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => handleRoute(item.to)}
                      className="text-left px-4 py-3 rounded-lg font-medium hover:bg-app-surface-muted dark:hover:bg-gray-800"
                    >
                      {item.label}
                    </button>
                  );
                }
                return (
                  <div key={item.label} className="rounded-lg">
                    <button
                      type="button"
                      onClick={() => setMobileFeaturesOpen((o) => !o)}
                      className="flex w-full items-center justify-between px-4 py-3 rounded-lg font-medium hover:bg-app-surface-muted dark:hover:bg-gray-800"
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileFeaturesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileFeaturesOpen ? (
                      <div className="ml-3 border-l-2 border-app-border dark:border-gray-700 pl-2 mb-2">
                        {item.items.map((sub) => (
                          <button
                            key={sub.label}
                            type="button"
                            onClick={() => scrollToAnchor(sub.href)}
                            className="block w-full text-left px-3 py-2 text-sm text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-white"
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-app-border/20 dark:border-gray-800/60 space-y-2">
              <Button className="w-full" onClick={() => { setNavOpen(false); navigate(`${ROUTES.LOGIN}?mode=admin`); }}>
                School login
              </Button>
              <Button variant="outline" className="w-full" onClick={() => { setNavOpen(false); navigate(ROUTES.PRICING); }}>
                View plans
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};
